import { useState, useCallback } from "react";
import { AlertTriangle, FileText, Mail, ListOrdered, Share2, Copy, Check, Download, Rocket, Pencil, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useNavigate } from "react-router-dom";
import jsPDF from "jspdf";
import { parseSections, type ParsedSection, type ParsedBlock } from "./parser";

// ─── Category helpers ─────────────────────────────────────────────
type CardCategory = "lead-magnet" | "optin-copy" | "email-sequence" | "promo-strategy" | "other";

function categoriseSection(title: string): CardCategory {
  const t = title.toLowerCase();
  if (t.includes("lead magnet") || t.includes("freebie") || t.includes("checklist") || t.includes("deliverable")) return "lead-magnet";
  if (t.includes("opt-in") || t.includes("optin") || t.includes("landing page") || t.includes("signup") || t.includes("sign-up")) return "optin-copy";
  if (t.includes("email") && (t.includes("sequence") || t.includes("welcome") || t.includes("follow") || t.includes("series") || t.includes("nurture"))) return "email-sequence";
  if (t.includes("promo") || t.includes("social") || t.includes("strategy") || t.includes("distribution") || t.includes("traffic")) return "promo-strategy";
  return "other";
}

const cardConfig: Record<CardCategory, { icon: typeof FileText; accentClass: string; borderClass: string }> = {
  "lead-magnet": { icon: FileText, accentClass: "bg-primary/20 text-primary", borderClass: "border-t-primary" },
  "optin-copy": { icon: Mail, accentClass: "bg-secondary/20 text-secondary", borderClass: "border-t-secondary" },
  "email-sequence": { icon: ListOrdered, accentClass: "bg-primary/20 text-primary", borderClass: "border-t-primary" },
  "promo-strategy": { icon: Share2, accentClass: "bg-secondary/20 text-secondary", borderClass: "border-t-secondary" },
  "other": { icon: FileText, accentClass: "bg-muted text-muted-foreground", borderClass: "border-t-border" },
};

// ─── Tiny components ──────────────────────────────────────────────
function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success("Copied to clipboard");
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <Button variant="ghost" size="sm" onClick={handleCopy} className="text-muted-foreground hover:text-foreground gap-1.5 h-8 px-2">
      {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
      <span className="text-xs">{copied ? "Copied" : "Copy"}</span>
    </Button>
  );
}

// ─── Inline‑editable text block ───────────────────────────────────
function EditableText({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);

  if (!editing) {
    return (
      <div className="group relative">
        <div className="rounded-lg bg-muted/50 px-4 py-3 text-sm text-foreground/90 whitespace-pre-line leading-relaxed cursor-pointer hover:ring-1 hover:ring-primary/30 transition-all" onClick={() => { setDraft(value); setEditing(true); }}>
          {value}
        </div>
        <button onClick={() => { setDraft(value); setEditing(true); }} className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-muted">
          <Pencil className="w-3.5 h-3.5 text-muted-foreground" />
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <Textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={Math.max(3, draft.split("\n").length)} className="text-sm" autoFocus />
      <div className="flex gap-2">
        <Button size="sm" variant="default" onClick={() => { onChange(draft); setEditing(false); }} className="h-7 text-xs">Save</Button>
        <Button size="sm" variant="ghost" onClick={() => setEditing(false)} className="h-7 text-xs"><X className="w-3 h-3 mr-1" />Cancel</Button>
      </div>
    </div>
  );
}

// ─── Block renderer ───────────────────────────────────────────────
function BlockRenderer({ block, onUpdate }: { block: ParsedBlock; onUpdate?: (b: ParsedBlock) => void }) {
  if (block.type === "label-content") {
    return (
      <div className="mb-3">
        <span className="block text-[11px] uppercase tracking-[0.1em] text-muted-foreground mb-1">{block.label}</span>
        {block.content && (
          onUpdate
            ? <EditableText value={block.content} onChange={(v) => onUpdate({ ...block, content: v })} />
            : <div className="rounded-lg bg-muted/50 px-4 py-3 text-sm text-foreground">{block.content}</div>
        )}
      </div>
    );
  }
  if (block.type === "bullets") {
    return (
      <div className="rounded-lg bg-muted/50 px-4 py-3 mb-3">
        <ul className="space-y-2">
          {block.items?.map((item, ii) => (
            <li key={ii} className="flex items-start gap-2 text-sm text-foreground/90">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-primary shrink-0" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </div>
    );
  }
  return <p className="text-sm text-foreground/80 leading-relaxed mb-2">{block.content}</p>;
}

// ─── Email card with subject line variations ──────────────────────
function EmailCard({ block, onUpdate }: { block: ParsedBlock; onUpdate?: (b: ParsedBlock) => void }) {
  const subjectVariations = block.subjectVariations || [];
  const [selectedSubject, setSelectedSubject] = useState(0);

  return (
    <AccordionItem value={`email-${block.emailNumber}`} className="rounded-lg border border-border/40 bg-card/60 overflow-hidden">
      <AccordionTrigger className="px-4 py-3 hover:no-underline">
        <div className="flex items-center gap-3 text-left">
          <Badge className="bg-primary/20 text-primary border-primary/30 text-xs shrink-0">Email {block.emailNumber}</Badge>
          <span className="text-sm text-foreground font-medium truncate">{block.subject || `Email ${block.emailNumber}`}</span>
        </div>
      </AccordionTrigger>
      <AccordionContent className="px-4 pb-4 space-y-4">
        {/* Subject line variations */}
        {subjectVariations.length > 1 && (
          <div>
            <span className="block text-[11px] uppercase tracking-[0.1em] text-muted-foreground mb-2">Subject Line Variations (A/B Test)</span>
            <div className="space-y-1.5">
              {subjectVariations.map((sv, i) => (
                <button
                  key={i}
                  onClick={() => setSelectedSubject(i)}
                  className={`w-full text-left rounded-lg px-4 py-2.5 text-sm transition-all border ${
                    selectedSubject === i
                      ? "border-primary bg-primary/10 text-foreground"
                      : "border-border/40 bg-muted/30 text-muted-foreground hover:border-border"
                  }`}
                >
                  <span className="font-mono text-[10px] text-muted-foreground mr-2">V{i + 1}</span>
                  {sv}
                </button>
              ))}
            </div>
          </div>
        )}
        {subjectVariations.length <= 1 && block.subject && (
          <div>
            <span className="block text-[11px] uppercase tracking-[0.1em] text-muted-foreground mb-1">Subject Line</span>
            {onUpdate
              ? <EditableText value={block.subject} onChange={(v) => onUpdate({ ...block, subject: v })} />
              : <div className="rounded-lg bg-muted/50 px-4 py-3 text-sm text-foreground">{block.subject}</div>
            }
          </div>
        )}
        {block.body && (
          <div>
            <span className="block text-[11px] uppercase tracking-[0.1em] text-muted-foreground mb-1">Body</span>
            {onUpdate
              ? <EditableText value={block.body} onChange={(v) => onUpdate({ ...block, body: v })} />
              : <div className="rounded-lg bg-muted/50 px-4 py-3 text-sm text-foreground/90 whitespace-pre-line leading-relaxed">{block.body}</div>
            }
          </div>
        )}
      </AccordionContent>
    </AccordionItem>
  );
}

// ─── Section content ──────────────────────────────────────────────
function SectionContent({ section, isEmailSequence }: { section: ParsedSection; isEmailSequence: boolean }) {
  const [blocks, setBlocks] = useState(section.blocks);

  const updateBlock = useCallback((index: number, updated: ParsedBlock) => {
    setBlocks((prev) => prev.map((b, i) => (i === index ? updated : b)));
  }, []);

  if (isEmailSequence) {
    const emailCards = blocks.filter((b) => b.type === "email-card");
    const otherBlocks = blocks.filter((b) => b.type !== "email-card");
    return (
      <div className="space-y-3">
        {otherBlocks.length > 0 && (
          <div className="space-y-2 mb-4">
            {otherBlocks.map((b, i) => <BlockRenderer key={i} block={b} onUpdate={(u) => updateBlock(blocks.indexOf(b), u)} />)}
          </div>
        )}
        {emailCards.length > 0 && (
          <Accordion type="multiple" className="space-y-2">
            {emailCards.map((email, i) => (
              <EmailCard key={i} block={email} onUpdate={(u) => updateBlock(blocks.indexOf(email), u)} />
            ))}
          </Accordion>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {blocks.map((block, i) => <BlockRenderer key={i} block={block} onUpdate={(u) => updateBlock(i, u)} />)}
    </div>
  );
}

// ─── Main renderer ────────────────────────────────────────────────
export default function EmailResultsRenderer({ rawContent }: { rawContent: string }) {
  const sections = parseSections(rawContent);
  const navigate = useNavigate();

  const handleDownloadPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(18);
    doc.text("Email List Building System", 20, 20);
    let y = 35;
    sections.forEach((section) => {
      if (y > 260) { doc.addPage(); y = 20; }
      doc.setFontSize(14);
      doc.text(section.title, 20, y);
      y += 8;
      doc.setFontSize(10);
      const lines = doc.splitTextToSize(section.rawText.replace(/[#*`]/g, ""), 170);
      for (const line of lines) {
        if (y > 280) { doc.addPage(); y = 20; }
        doc.text(line, 20, y);
        y += 5;
      }
      y += 8;
    });
    doc.save("email-list-building-system.pdf");
    toast.success("PDF downloaded!");
  };

  const handleDownloadTXT = () => {
    const text = sections.map((s) => `=== ${s.title} ===\n\n${s.rawText}`).join("\n\n\n");
    const blob = new Blob([text], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "email-list-building-system.txt";
    a.click();
    URL.revokeObjectURL(url);
    toast.success("TXT downloaded!");
  };

  const handleCopyAll = () => {
    const text = sections.map((s) => `## ${s.title}\n\n${s.rawText}`).join("\n\n---\n\n");
    navigator.clipboard.writeText(text);
    toast.success("All content copied!");
  };

  return (
    <div className="space-y-6">
      {/* Top actions */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="flex items-start gap-3 p-3 rounded-xl bg-amber-500/10 border border-amber-500/20 flex-1">
          <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
          <p className="text-amber-200/80 text-xs">
            Replace all <span className="font-semibold text-amber-300">[bracketed placeholders]</span> with your own details. Click any text block to edit inline.
          </p>
        </div>
        <div className="flex gap-2 shrink-0 flex-wrap">
          <Button variant="outline" size="sm" onClick={handleCopyAll} className="gap-1.5">
            <Copy className="w-3.5 h-3.5" />Copy All
          </Button>
          <Button variant="outline" size="sm" onClick={handleDownloadPDF} className="gap-1.5">
            <Download className="w-3.5 h-3.5" />PDF
          </Button>
          <Button variant="outline" size="sm" onClick={handleDownloadTXT} className="gap-1.5">
            <Download className="w-3.5 h-3.5" />TXT
          </Button>
          <Button variant="default" size="sm" onClick={() => navigate("/app/launch-advisor")} className="gap-1.5">
            <Rocket className="w-3.5 h-3.5" />Send to Launch Advisor
          </Button>
        </div>
      </div>

      {/* Section cards */}
      <div className="space-y-5">
        {sections.map((section, si) => {
          const category = categoriseSection(section.title);
          const config = cardConfig[category];
          const Icon = config.icon;

          return (
            <div key={si} className={`rounded-xl border border-border/50 bg-card/60 overflow-hidden border-t-2 ${config.borderClass}`}>
              <div className="flex items-center justify-between px-5 py-4 border-b border-border/30">
                <div className="flex items-center gap-3">
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${config.accentClass}`}>
                    <Icon className="w-4.5 h-4.5" />
                  </div>
                  <h3 className="font-display font-semibold text-foreground">{section.title}</h3>
                </div>
                <CopyButton text={section.rawText} />
              </div>
              <div className="px-5 py-4">
                <SectionContent section={section} isEmailSequence={category === "email-sequence"} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
